import { useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { EnhancedNotificationItem } from '@/features/notifications/types/notifications.types'

const globalProcessedToasts = new Set<string>()
const globalInvalidationQueue = new Map<string, NodeJS.Timeout>()

function debouncedInvalidateQueries(queryClient: ReturnType<typeof useQueryClient>, queryKey: unknown[], delay = 500) {
    const keyString = JSON.stringify(queryKey)

    if (globalInvalidationQueue.has(keyString)) {
        clearTimeout(globalInvalidationQueue.get(keyString)!)
    }

    const timeout = setTimeout(async () => {
        await queryClient.invalidateQueries({
            queryKey,
            refetchType: 'active',
        })
        globalInvalidationQueue.delete(keyString)
    }, delay)

    globalInvalidationQueue.set(keyString, timeout)
}

export function useSyncNotificationHandler(notifications: EnhancedNotificationItem[]) {
    const queryClient = useQueryClient()
    const processedNotifications = useRef(new Set<string>())

    const handleSyncNotification = useCallback(
        (notification: EnhancedNotificationItem) => {
            if (processedNotifications.current.has(notification.id)) {
                return
            }

            const platformMatch =
                notification.internal_url?.match(/platform[=/](\w+)/) ||
                notification.title?.toLowerCase().match(/(slack|teams)/) ||
                notification.description?.toLowerCase().match(/(slack|teams)/)

            const platform = platformMatch ? (platformMatch[1] || platformMatch[0]).toLowerCase() : null

            if (!platform) {
                return
            }

            processedNotifications.current.add(notification.id)

            const frontendPlatform = `${platform}-bot` as const

            debouncedInvalidateQueries(queryClient, ['platformHealth', frontendPlatform])
            debouncedInvalidateQueries(queryClient, ['platformStats', frontendPlatform])
            debouncedInvalidateQueries(queryClient, ['organizationOverview'])
            debouncedInvalidateQueries(queryClient, ['getIntegrationsSummary'])

            const isOnConnectPage = window.location.pathname.includes('/connect')

            if (isOnConnectPage) {
                const platformName = platform.charAt(0).toUpperCase() + platform.slice(1)
                const toastKey = `${platform}-${notification.id}`

                if (globalProcessedToasts.has(toastKey)) {
                    return
                }

                globalProcessedToasts.add(toastKey)

                if (
                    notification.title?.toLowerCase().includes('completed') ||
                    notification.description?.toLowerCase().includes('completed') ||
                    notification.description?.toLowerCase().includes('successful')
                ) {
                    toast.success(`${platformName} sync completed successfully!`)
                } else if (
                    notification.title?.toLowerCase().includes('failed') ||
                    notification.description?.toLowerCase().includes('failed') ||
                    notification.description?.toLowerCase().includes('error')
                ) {
                    toast.error(`${platformName} sync failed. Check the integration status for details.`)
                }
            }
        },
        [queryClient],
    )

    useEffect(() => {
        const currentNotificationIds = new Set(notifications.map((n) => n.id))
        const processedIds = Array.from(processedNotifications.current)

        processedIds.forEach((id) => {
            if (!currentNotificationIds.has(id)) {
                processedNotifications.current.delete(id)
            }
        })

        const currentToastKeys = new Set(
            notifications.flatMap((n) => {
                const platformMatch =
                    n.internal_url?.match(/platform[=/](\w+)/) ||
                    n.title?.toLowerCase().match(/(slack|teams)/) ||
                    n.description?.toLowerCase().match(/(slack|teams)/)
                const platform = platformMatch ? (platformMatch[1] || platformMatch[0]).toLowerCase() : null
                return platform ? [`${platform}-${n.id}`] : []
            }),
        )

        Array.from(globalProcessedToasts).forEach((toastKey) => {
            if (!currentToastKeys.has(toastKey)) {
                globalProcessedToasts.delete(toastKey)
            }
        })

        const syncNotifications = notifications.filter((notification) => {
            const title = notification.title?.toLowerCase() || ''
            const description = notification.description?.toLowerCase() || ''

            const isSyncNotification =
                (title.includes('sync') || description.includes('sync')) &&
                (title.includes('slack') ||
                    title.includes('teams') ||
                    description.includes('slack') ||
                    description.includes('teams'))

            return isSyncNotification && !notification.is_dismissed
        })

        syncNotifications.forEach(handleSyncNotification)
    }, [notifications, handleSyncNotification])
}
